import { TAuthLoginRequestPayload, authLoginRequestSchema, authService } from "@/api/auth";
import { useNavigate, useRouter, useSearch } from "@tanstack/react-router";
import { FormEvent, useCallback } from "react";
import { DefaultValues, useForm } from "react-hook-form";

import { userService } from "@/api/user/user.service";
import { legalService } from "@/api/legal";
import { useToast } from "@/components/ui";
import { useStore } from "@/store";
import { getPostLoginRedirectPath } from "@/utils/authUtils";
import { handleFormError } from "@/utils/formErrorUtils";
import { getJwtExpiration } from "@/utils/jwt";
import { hasAdminRole } from "@/utils/routeGuards";
import { zodResolver } from "@hookform/resolvers/zod";

export type TUseLoginFormProps = {
  defaultValues?: DefaultValues<TAuthLoginRequestPayload>;
};

export const useLoginForm = ({ defaultValues }: TUseLoginFormProps = {}) => {
  const form = useForm<TAuthLoginRequestPayload>({
    resolver: zodResolver(authLoginRequestSchema),
    defaultValues,
  });

  const { toast } = useToast();

  const router = useRouter();
  const navigate = useNavigate();
  const { from: returnPath } = useSearch({ from: "/auth/login" });

  const updateAuthStore = useStore.use.updateAuthStore();
  const invalidateCompanies = useStore.use.invalidateCompanies();

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      form
        .handleSubmit(async (payload) => {
          const { data, error } = await authService.login(payload);

          if (data) {
            // Decode JWT to get access token expiration
            const accessTokenExpiration: number | null = getJwtExpiration(data.data.access_token);

            // update auth store with new accessToken and expiresAt
            updateAuthStore({
              accessToken: data.data.access_token,
              expiresAt: accessTokenExpiration ?? undefined,
            });

            // Check if 2FA is required
            if (data.data.requires_2fa) {
              // Navigate to OTP verification page
              void navigate({
                to: "/auth/verify-otp",
                search: { mode: undefined, phone: undefined, email: undefined, from: returnPath },
              });
              return;
            }

            // If 2FA is not required, continue with normal login flow
            // fetch and save user profile
            const { data: profileData, error: profileError } = await userService.getProfile();

            if (profileData) {
              updateAuthStore({
                profile: profileData,
              });
            }

            if (profileError) {
              console.error("[auth] Failed to fetch profile after login", { error: profileError });
              // Don't block login if profile fetch fails
            }

            // Fetch and save user roles
            const { data: rolesData, error: rolesError } = await userService.getRoles();

            if (rolesData) {
              updateAuthStore({
                roles: rolesData,
              });
            }

            if (rolesError) {
              // Don't block login — roles can be re-fetched later — but surface
              // a non-blocking warning so the user can refresh if they see issues.
              toast({
                variant: "default",
                title: "Session partially loaded",
                description:
                  "We couldn't load your permissions. Some features may be unavailable. Please refresh if you experience any issues.",
              });
            }

            // Invalidate companies cache to ensure fresh data is fetched after login
            invalidateCompanies();

            // Post-auth verification flow:
            // If user has NOT verified either email or phone, force them into the verification method selection.
            if (profileData && !profileData.email_verified && !profileData.phone_verified) {
              await router
                .invalidate()
                .finally(() => {
                  void navigate({ to: "/auth/verify-method" });
                })
                .catch(console.error);
              return;
            }

            const { data: legalStatusData } = await legalService.getStatus();
            if (legalStatusData && !legalStatusData.data.all_required_accepted) {
              await router
                .invalidate()
                .finally(() => {
                  void navigate({
                    to: "/auth/accept-legal",
                    search: { from: returnPath },
                  });
                })
                .catch(console.error);
              return;
            }

            const redirectPath = getPostLoginRedirectPath({
              from: returnPath,
              isAdmin: hasAdminRole(rolesData),
            });

            // invalidate router
            await router
              .invalidate()
              .finally(() => {
                window.location.assign(redirectPath);
              })
              .catch(console.error);
          }

          if (error) {
            // Field errors bind to inputs; invalid credentials and other
            // non-field failures surface as a form-level banner (<FormRootError />).
            await handleFormError(error, form);
          }
        })(e)
        .catch(console.error);
    },
    [form, toast, updateAuthStore, invalidateCompanies, navigate, router, returnPath]
  );

  return {
    form,
    handleSubmit,
  };
};
